import { AfterViewInit, ChangeDetectorRef, Component, Inject, Input, NgZone, OnDestroy, OnInit, OnChanges, PLATFORM_ID } from '@angular/core';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5percent from '@amcharts/amcharts5/percent';
import * as am5plugins_sliceGrouper from '@amcharts/amcharts5/plugins/sliceGrouper';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { CommonService } from '../../../../shared/services/common.service';
import { SpinnerService } from '../../../../shared/services/spinner.service';

/**
 * Pie Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-pie-graph',
  standalone: false,
  templateUrl: './pie-graph.component.html',
  styleUrl: './pie-graph.component.scss'
})
export class PieGraphComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  private root!: am5.Root;
  data: BarChartData[] = [];
  private pieGraphReportSubscription: any;
  private isPieViewInitialized = false;
  private isCreatingChart = false;

  @Input() reportData: any = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _commonService: CommonService,
    private _spinnerService: SpinnerService) { }

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.pieGraphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Pie Graph Data", this.reportData);
          this.data = this._baseChartService.transformReportData(data);
          if (this.isPieViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isPieViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        const chartContainer = document.getElementById('pie-chartdiv');
        if (chartContainer) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          this.updateChart(this.data);
        } else {
          console.log('Pie Chart - DOM element not ready in ngAfterViewInit, will retry later');
        }
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0 && this.isPieViewInitialized) {
      this.zone.runOutsideAngular(() => {
        const chartContainer = document.getElementById('pie-chartdiv');
        if (chartContainer) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          this.updateChart(this.data);
        } else {
          console.log('Pie Chart - DOM element not ready in ngOnChanges, will retry later');
          setTimeout(() => {
            const retryContainer = document.getElementById('pie-chartdiv');
            if (retryContainer) {
              this.data = this._baseChartService.transformReportData(this.reportData);
              this.updateChart(this.data);
            }
          }, 100);
        }
      });
    }
  }

  createChart(data: BarChartData[]) {
    if (this.isCreatingChart) {
      console.log('Pie Chart - Chart creation already in progress, skipping...');
      return;
    }

    this.isCreatingChart = true;

    const chartContainer = document.getElementById('pie-chartdiv');
    if (!chartContainer) {
      console.error('Pie Chart - Chart container not found! Make sure element with id "pie-chartdiv" exists');
      this.isCreatingChart = false;
      return;
    }

    if (this.root) {
      console.log('Pie Chart - Disposing existing chart...');
      try {
        this._baseChartService.disposeChart(this.root);
      } catch (error) {
        console.warn('Pie Chart - Error disposing existing chart:', error);
      }
      this.root = undefined as any;
    }

    try {
      const container = document.getElementById('pie-chartdiv');
      if (container && container.children.length > 0) {
        console.log('Pie Chart - Found existing container children, cleaning up...');
        container.innerHTML = '';
      }
    } catch (error) {
      console.warn('Pie Chart - Error checking existing container:', error);
    }

    try {
      console.log('Pie Chart - Creating new chart...');
      this.root = this._baseChartService.createRoot('pie-chartdiv');
      this._baseChartService.attachExporting(this.root, { 
        filePrefix: 'pie',
        data: data
      });

      const categoryField = this._baseChartService.getCategoryField(data);
      const valueFields = this._baseChartService.getValueFields(data);
      const valueField = valueFields[0]; // Use only the first value field

      const pieChart = am5percent.PieChart.new(this.root, {
        layout: this.root.verticalLayout
      });

      // Enhanced chart animation
      this._baseChartService.setupChartAnimation(pieChart, 'elastic');

      this.root.container.children.push(pieChart);

      // Create a single pie series
      let pieSeries = pieChart.series.push(
        am5percent.PieSeries.new(this.root, {
          valueField: valueField,
          categoryField: categoryField
        })
      );

      // Enhanced series animation
      this._baseChartService.setupSeriesAnimation(pieSeries, 'bounce');

      // Configure slices
      pieSeries.slices.template.setAll({
        stroke: am5.color("#fff"),
        strokeWidth: 2
      });

      // Tooltip background matches the hovered slice color
      const tooltip = am5.Tooltip.new(this.root, {
        getFillFromSprite: true,
        labelText: `{category}: {valuePercentTotal.formatNumber("0.00")}%\n{value.formatNumber("#,##0")}`,
        pointerOrientation: 'horizontal'
      });
      tooltip.label.set('fill', am5.color(0xFFFFFF));
      this._baseChartService.setupTooltipAnimation(tooltip);
      pieSeries.slices.template.set('tooltip', tooltip);

      // Hide default labels and ticks
      pieSeries.labels.template.setAll({
        visible: false
      });
      pieSeries.ticks.template.setAll({
        visible: false
      });

      // Add value labels directly on slices using bullets
      pieSeries.slices.template.set("tooltipY", 0);
      pieSeries.bullets.push((root, series, dataItem) => {
        return am5.Bullet.new(root, {
          sprite: am5.Label.new(root, {
            text: "{value}",
            fill: am5.color(0x000000),
            centerX: am5.p50,
            centerY: am5.p50,
            populateText: true,
            fontSize: 14,
            fontWeight: "500"
          })
        });
      });

      // Set data first
      pieSeries.data.setAll(data);

      // Add slice grouper plugin - groups slices beyond first 5 into "Others"
      // The plugin automatically handles drill-down and back button
      let grouper = am5plugins_sliceGrouper.SliceGrouper.new(this.root, {
        series: pieSeries,
        clickBehavior: "break",
        threshold: 5,
        groupName: "Others"
      });

      // Create legend
      let legend = pieChart.children.push(
        this._baseChartService.createLegend(this.root)
      );

      let markerTemplate = legend.markers.template;
      markerTemplate.set('width', 12);
      markerTemplate.set('height', 12);

      const updateLegend = () => legend.data.setAll(pieSeries.dataItems);

      setTimeout(() => updateLegend(), 100);

      pieSeries.slices.template.events.on("click", () => {
        setTimeout(() => updateLegend(), 50);
      });

      // Enhanced legend animation
      this._baseChartService.setupLegendAnimation(legend);

      // Trigger enhanced chart animation
      setTimeout(() => {
        pieSeries.appear(1000, 100);
      }, 100);

      console.log('Pie Chart - Chart created successfully');
      this.isCreatingChart = false;

    } catch (error) {
      console.error('Error creating pie chart:', error);
      this.isCreatingChart = false;
      if (this.root) {
        try {
          this._baseChartService.disposeChart(this.root);
        } catch (disposeError) {
          console.warn('Pie Chart - Error disposing chart on error:', disposeError);
        }
        this.root = undefined as any;
      }
    }
  }

  private updateChart(data: BarChartData[]) {
    if (this.isCreatingChart) {
      console.log('Pie Chart - Chart update skipped, chart creation in progress...');
      return;
    }

    const chartContainer = document.getElementById('pie-chartdiv');
    if (!chartContainer) {
      console.log('Pie Chart - DOM element not ready in updateChart, will retry later');
      setTimeout(() => this.updateChart(data), 100);
      return;
    }

    this.data = data;

    if (this.root) {
      console.log('Pie Chart - Disposing chart in updateChart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }

    setTimeout(() => {
      this.createChart(data);
    }, 200);

    BaseChartService.updateActiveChartData(data);
  }

  ngOnDestroy(): void {
    if (this.root) {
      console.log('Pie Chart - Disposing chart in ngOnDestroy...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }
    if (this.pieGraphReportSubscription) {
      this.pieGraphReportSubscription.unsubscribe();
    }
    this.isCreatingChart = false;
    this.isPieViewInitialized = false;
    this._spinnerService.hide();
  }
}
